// src/pages/AuditLogs.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { AuditLogDto } from '../types/dtos'; // Assuming you have a shared AuditLog DTO
import LoadingSpinner from '../components/LoadingSpinner';
import { Download } from 'lucide-react';

export default function AuditLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        // ✅ Fetch audit logs from .NET Admin API
        const res = await apiClient.get<AuditLogDto[]>('/admin/logs');
        setLogs(res.data);
        setError(null);
      } catch (err: any) {
        console.error('[AuditLogs] Failed to load audit logs:', err);
        setError(err.response?.data?.message || 'Unable to load audit logs. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto">

          {/* Breadcrumb (PRD Section 7: "Interactive elements with feedback") */}
          <nav className="mb-6 text-sm text-gray-500" aria-label="Breadcrumb">
            <ol className="list-none p-0 inline-flex">
              <li className="flex items-center">
                <a href="/admin" className="text-blue-600 hover:underline">Home</a>
                <svg className="fill-current w-3 h-3 mx-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z" />
                </svg>
              </li>
              <li>
                <span className="text-gray-500">Audit Logs</span>
              </li>
            </ol>
          </nav>

          {/* PRD Section 4: "Logs & Monitoring" - Header Card */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Audit Logs</h1>
                <p className="mt-1 text-sm text-gray-500">Immutable logs of all system activities</p>
              </div>
              <button
                onClick={() => navigate('/logs/export')} // Navigate to export page
                className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-700 to-indigo-800 text-white rounded-lg text-sm font-medium hover:from-blue-800 hover:to-indigo-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md flex items-center justify-center transition-all"
                aria-label="Export audit logs"
              >
                <Download size={18} className="mr-2" />
                <span>Export Logs</span>
              </button>
            </div>
          </div>

          {/* PRD Section 4: "Logs & Monitoring" - Audit Logs Table */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            {loading ? (
              <div className="flex justify-center items-center min-h-[200px]" aria-live="polite">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700" role="alert">
                {error}
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-gray-600">No audit logs found.</p>
                <p className="text-gray-600 mt-2">System activities will appear here.</p>
              </div>
            ) : (
              <div className="bg-white">
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 text-gray-600 font-bold border-b">
                      <tr>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Action</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Resource</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">Timestamp</th>
                        <th scope="col" className="px-6 py-4 text-left text-xs uppercase tracking-wider">IP Address</th>
                        <th scope="col" className="px-6 py-4 text-right text-xs uppercase tracking-wider font-bold">Details</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {logs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-gray-900">{log.action}</div>
                            <div className="text-[10px] text-gray-400 font-mono">User: {log.userId}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 font-medium">{log.resource}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {new Date(log.timestamp).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-400 font-mono">{log.ipAddress}</td>
                          <td className="px-6 py-4 text-right text-xs text-gray-500 max-w-xs truncate font-medium">
                            {log.details}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                  {logs.map((log) => (
                    <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-[10px] text-blue-600 font-bold uppercase">{log.action}</p>
                          <h4 className="text-sm font-bold text-gray-900">{log.resource}</h4>
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium">
                          {new Date(log.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-3 p-2 bg-gray-50 rounded bg-opacity-50">
                        <div className="text-[10px] text-gray-500 font-mono truncate max-w-[150px]">
                          IP: {log.ipAddress}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          U: {log.userId.substring(0, 8)}...
                        </div>
                      </div>
                      <p className="mt-2 text-[11px] text-gray-600 font-medium truncate italic">
                        "{log.details}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
}